import type { SVGProps } from 'react';

import { cn } from '../lib/cn';
import { ICON_STROKE, iconSize, type IconSize } from '../tokens';
import { icons, type IconName } from './registry';

/**
 * Colour of the line.
 *
 * `current` takes the text colour of the control around it — a `Button`, an
 * `IconButton`, a rail item — so hover and disabled follow the label without a
 * second rule. Everywhere else the icon names its own colour, like every other
 * element in this design system.
 */
export type IconTone =
    | 'current'
    | 'primary'
    | 'secondary'
    | 'muted'
    | 'brand'
    | 'onDark'
    | 'onDarkMuted'
    | 'warn'
    | 'danger';

const toneClass: Record<IconTone, string> = {
    current: '',
    primary: 'text-[var(--text-primary)]',
    secondary: 'text-[var(--text-secondary)]',
    muted: 'text-[var(--text-tertiary-safe)]',
    brand: 'text-[var(--accent-dark)]',
    onDark: 'text-[var(--text-on-dark-2)]',
    onDarkMuted: 'text-[var(--text-on-dark-3)]',
    warn: 'text-[var(--warn)]',
    danger: 'text-[var(--danger)]',
};

export type IconProps = Omit<
    SVGProps<SVGSVGElement>,
    'children' | 'ref' | 'color' | 'stroke' | 'strokeWidth' | 'width' | 'height' | 'name'
> & {
    name: IconName;
    /** 16 · 18 · 20px (`--icon-sm/md/lg`). */
    size?: IconSize;
    tone?: IconTone;
    /**
     * Accessible name. Without one the icon is decorative and hidden from
     * assistive technology — the normal case, because a label sits next to it.
     * An icon-only control names itself (`IconButton` requires `aria-label`).
     */
    label?: string;
};

/**
 * The one way to draw an icon: a lucide line icon at a design-system size, with
 * the design-system stroke, in a design-system colour.
 *
 * The stroke does not scale with the size, so a 16px icon in the rail and a
 * 20px icon in a panel header draw the same 1.5px line.
 */
export function Icon({
    name,
    size = 'md',
    tone = 'current',
    label,
    className,
    ...rest
}: IconProps) {
    const Glyph = icons[name];

    return (
        <Glyph
            size={iconSize[size]}
            strokeWidth={ICON_STROKE}
            nonScalingStroke
            focusable="false"
            data-icon={name}
            {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
            className={cn('block shrink-0', toneClass[tone], className)}
            {...rest}
        />
    );
}
